<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog";

  import { overlay } from "$lib/stores/ui";
  import { user, doLogin } from "$lib/stores/auth";
  import { t, translate, type TranslationKey } from "$lib/i18n";
  import { GetUserDashboard, PatchUser, DeleteUser } from "$lib/services/requests";
  import type { UserDashboardEntry } from "@briefmarken_katalog/types";
  import type { Role } from "@briefmarken_katalog/types";
  import PenIcon from "@lucide/svelte/icons/pen";
  import CheckIcon from "@lucide/svelte/icons/check";
  import XIcon from "@lucide/svelte/icons/x";

  const isOpen = overlay('userDashboard');

  let entries = $state<UserDashboardEntry[] | null>(null);
  let loadError = $state(false);
  let selectedEntry = $state<UserDashboardEntry | null>(null);

  type EditField = 'name' | 'email' | 'role';
  let editingCell = $state<{ userId: string; field: EditField } | null>(null);
  let editValue = $state('');
  let saving = $state(false);

  async function load() {
    entries = null;
    loadError = false;
    try {
      entries = await new GetUserDashboard(doLogin).execute();
    } catch {
      loadError = true;
    }
  }

  $effect(() => {
    if ($isOpen) load();
  });

  function startEdit(entry: UserDashboardEntry, field: EditField) {
    editingCell = { userId: entry._id, field };
    editValue = field === 'name' ? (entry.name ?? '') : entry[field];
  }

  function cancelEdit() {
    editingCell = null;
    editValue = '';
  }

  async function saveEdit(entry: UserDashboardEntry) {
    if (!editingCell) return;
    saving = true;
    try {
      const patch = { [editingCell.field]: editValue };
      await new PatchUser(entry._id, patch, doLogin).execute();
      entries = entries!.map(e =>
        e._id === entry._id ? { ...e, ...patch } as UserDashboardEntry : e
      );
    } catch {
      // leave edit mode open on error so user can retry
    } finally {
      saving = false;
      editingCell = null;
      editValue = '';
    }
  }

  const roles: Role[] = ['Viewer', 'Contributor', 'Reviewer', 'Admin'];

  async function deleteUser(entry: UserDashboardEntry) {
    try {
      await new DeleteUser(entry._id, doLogin).execute();
      entries = entries!.filter(e => e._id !== entry._id);
    } catch (e: any) {
      alert(translate('errors.deleteUser') + e.message);
    }
  }

  let detailOpen = $derived(selectedEntry !== null);

  function closeDetail() {
    selectedEntry = null;
  }
</script>

<Dialog bind:open={$isOpen}>
  <DialogContent maxWidth="min(56rem, calc(100vw - 2rem))" class="w-full">
    <DialogHeader>
      <DialogTitle>{$t('userDashboard.title')}</DialogTitle>
      <DialogDescription>{$t('userDashboard.description')}</DialogDescription>
    </DialogHeader>

    {#if loadError}
      <p class="text-sm text-red-500">{$t('userDashboard.error')}</p>
    {:else if entries === null}
      <p class="text-sm text-muted-foreground">{$t('userDashboard.loading')}</p>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b text-left">
              <th class="py-2 pr-2"></th>
              <th class="py-2 pr-4 font-semibold">{$t('userDashboard.id')}</th>
              <th class="py-2 pr-4 font-semibold">{$t('userDashboard.name')}</th>
              <th class="py-2 pr-4 font-semibold">{$t('userDashboard.email')}</th>
              <th class="py-2 pr-1 font-semibold">{$t('userDashboard.role')}</th>
            </tr>
          </thead>
          <tbody>
            {#each entries as entry}
              {@const isEditingName = editingCell?.userId === entry._id && editingCell.field === 'name'}
              {@const isEditingEmail = editingCell?.userId === entry._id && editingCell.field === 'email'}
              {@const isEditingRole = editingCell?.userId === entry._id && editingCell.field === 'role'}
              <tr class="border-b last:border-0 hover:bg-muted/40">
                <td class="py-2 pr-2">
                  {#if entry.email !== $user?.email}
                  <button
                    class="text-red-600 hover:text-red-700"
                    onclick={() => deleteUser(entry)}
                  ><XIcon class="size-4" /></button>
                  {/if}
                </td>
                <td class="py-2 pr-4">
                  <button
                    class="font-mono text-xs text-blue-600 hover:underline cursor-pointer"
                    onclick={() => selectedEntry = entry}
                  >{entry._id}</button>
                </td>

                <!-- Name -->
                <td class="py-2 pr-4">
                  {#if isEditingName}
                    <span class="flex items-center gap-1">
                      <!-- svelte-ignore a11y_autofocus -->
                      <!-- usage of autofocus seems legit.
                        A sighted keyboard user will benefit from the automatic focus 
                        and the autofocus does not apply at on-load so that it would not confused screen readers -->
                      <input
                        class="border rounded px-1 py-0.5 text-sm w-32"
                        bind:value={editValue}
                        onkeydown={(e) => { if (e.key === 'Enter') saveEdit(entry); if (e.key === 'Escape') cancelEdit(); }}
                        autofocus
                      />
                      <button class="text-green-600 hover:text-green-700" disabled={saving} onclick={() => saveEdit(entry)}><CheckIcon class="size-3.5" /></button>
                      <button class="text-muted-foreground hover:text-foreground" onclick={cancelEdit}><XIcon class="size-3.5" /></button>
                    </span>
                  {:else}
                    <span class="flex items-center gap-1 group">
                      <span>{entry.name ?? '—'}</span>
                      <button class="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" onclick={() => startEdit(entry, 'name')}><PenIcon class="size-3" /></button>
                    </span>
                  {/if}
                </td>

                <!-- Email -->
                <td class="py-2 pr-4">
                  {#if isEditingEmail}
                    <span class="flex items-center gap-1">
                      <!-- svelte-ignore a11y_autofocus -->
                      <input
                        class="border rounded px-1 py-0.5 text-sm w-44"
                        bind:value={editValue}
                        onkeydown={(e) => { if (e.key === 'Enter') saveEdit(entry); if (e.key === 'Escape') cancelEdit(); }}
                        autofocus
                      />
                      <button class="text-green-600 hover:text-green-700" disabled={saving} onclick={() => saveEdit(entry)}><CheckIcon class="size-3.5" /></button>
                      <button class="text-muted-foreground hover:text-foreground" onclick={cancelEdit}><XIcon class="size-3.5" /></button>
                    </span>
                  {:else}
                    <span class="flex items-center gap-1 group">
                      <span>{entry.email}</span>
                      <button class="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" onclick={() => startEdit(entry, 'email')}><PenIcon class="size-3" /></button>
                    </span>
                  {/if}
                </td>

                <!-- Role -->
                <td class="py-2 pr-1">
                  {#if isEditingRole}
                    <span class="flex items-center gap-1">
                      <select class="border rounded px-1 py-0.5 text-sm" bind:value={editValue}>
                        {#each roles as r}
                          <option value={r}>{$t(`roles.${r}` as TranslationKey)}</option>
                        {/each}
                      </select>
                      <button class="text-green-600 hover:text-green-700" disabled={saving} onclick={() => saveEdit(entry)}><CheckIcon class="size-3.5" /></button>
                      <button class="text-muted-foreground hover:text-foreground" onclick={cancelEdit}><XIcon class="size-3.5" /></button>
                    </span>
                  {:else}
                    <span class="flex items-center gap-1 group">
                      <span>{$t(`roles.${entry.role}` as TranslationKey)}</span>
                      <button class="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" onclick={() => startEdit(entry, 'role')}><PenIcon class="size-3" /></button>
                    </span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </DialogContent>
</Dialog>

{#if selectedEntry !== null}
  {@const entry = selectedEntry}
  <Dialog open={detailOpen} onOpenChange={(open) => { if (!open) closeDetail(); }}>
    <DialogContent maxWidth="min(40rem, calc(100vw - 2rem))" class="w-full">
      <DialogHeader>
        <DialogTitle>{$t('userDetail.title')}</DialogTitle>
        <DialogDescription>{entry.name ?? entry.email} · {$t(`roles.${entry.role}` as TranslationKey)}</DialogDescription>
      </DialogHeader>

      <div class="space-y-4 text-sm">
        <div>
          <p class="font-semibold mb-1">{$t('userDetail.suggestions')}</p>
          <table class="w-full border-collapse">
            <tbody>
              <tr class="border-b">
                <td class="py-1 text-muted-foreground">{$t('userDetail.suggestionsTotal')}</td>
                <td class="py-1 text-right font-medium">{entry.suggestionStats.total}</td>
              </tr>
              <tr class="border-b">
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.accepted')}</td>
                <td class="py-1 text-right">{entry.suggestionStats.accepted}</td>
              </tr>
              <tr class="border-b">
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.rejected')}</td>
                <td class="py-1 text-right">{entry.suggestionStats.rejected}</td>
              </tr>
              <tr class="border-b">
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.inReview')}</td>
                <td class="py-1 text-right">{entry.suggestionStats.inReview}</td>
              </tr>
              <tr>
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.notInReview')}</td>
                <td class="py-1 text-right">{entry.suggestionStats.notInReview}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <p class="font-semibold mb-1">{$t('userDetail.reviews')}</p>
          <table class="w-full border-collapse">
            <tbody>
              <tr class="border-b">
                <td class="py-1 text-muted-foreground">{$t('userDetail.reviewsTotal')}</td>
                <td class="py-1 text-right font-medium">{entry.reviewStats.total}</td>
              </tr>
              <tr class="border-b">
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.accepted')}</td>
                <td class="py-1 text-right">{entry.reviewStats.accepted}</td>
              </tr>
              <tr class="border-b">
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.rejected')}</td>
                <td class="py-1 text-right">{entry.reviewStats.rejected}</td>
              </tr>
              <tr>
                <td class="py-1 pl-4 text-muted-foreground">{$t('userDetail.inReview')}</td>
                <td class="py-1 text-right">{entry.reviewStats.inReview}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </DialogContent>
  </Dialog>
{/if}
